import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { People } from "@/api/endpoints";
import type { AccountPeople, ContactTouch } from "@/api/types";
import { personRole } from "@/lib/colors";
import { fmtDayShort } from "@/lib/format";
import { daysSince } from "@/lib/people";
import { Empty } from "@/components/ui";

const W = 640;
const ROW = 74;
const LEFT_X = 96;
const RIGHT_X = W - 96;
const TOP = 96;

type Node = {
  id: string;
  label: string;
  sub: string;
  x: number;
  y: number;
  color: string;
  dim?: boolean;
};

function recencyOpacity(lastTouch: string | null): number {
  const d = daysSince(lastTouch);
  if (d == null) return 0.18;
  if (d <= 14) return 0.9;
  if (d <= 30) return 0.6;
  if (d <= 60) return 0.38;
  return 0.22;
}

/** Who talks to whom at an account: their people on the left, ours on the
 *  right, brokers underneath. Line weight = how often, brightness = how recently. */
export default function RelationshipMap({
  accountId,
  accountName,
}: {
  accountId: string;
  accountName: string;
}) {
  const peopleQuery = useQuery({
    queryKey: ["account-people", accountId],
    queryFn: () => People.forAccount(accountId),
  });

  if (peopleQuery.isLoading) return <p className="p-6 text-md text-mut-500">Loading…</p>;
  const data = peopleQuery.data;
  if (!data || data.contacts.length === 0) {
    return (
      <div className="panel">
        <Empty title="No people on this account yet">
          Link contacts to this account's deals, then log calls and meetings with them. The map
          fills in from that history.
        </Empty>
      </div>
    );
  }
  return <MapView data={data} accountName={accountName} />;
}

function MapView({ data, accountName }: { data: AccountPeople; accountName: string }) {
  const [hover, setHover] = useState<string | null>(null);

  const layout = useMemo(() => {
    const theirs = data.contacts.filter((c) => c.kind === "account_contact");
    const brokers = data.contacts.filter((c) => c.kind === "broker_contact");
    const ours = data.team;
    const rows = Math.max(theirs.length, ours.length, 1);
    const brokerY = TOP + rows * ROW + 24;
    const H = brokerY + (brokers.length ? 70 : 0);

    const nodes = new Map<string, Node>();
    theirs.forEach((c, i) =>
      nodes.set(`c:${c.contact_id}`, {
        id: `c:${c.contact_id}`,
        label: c.name,
        sub:
          [c.role, (daysSince(c.last_touch) ?? 999) > 30 ? "cooling" : null]
            .filter(Boolean)
            .join(" · ") ||
          c.title ||
          "",
        x: LEFT_X,
        y: TOP + i * ROW,
        color: "rgb(var(--cyan))",
        dim: (daysSince(c.last_touch) ?? 999) > 30,
      }),
    );
    ours.forEach((m, i) =>
      nodes.set(`u:${m.name}`, {
        id: `u:${m.name}`,
        label: m.name,
        sub: m.role === "primary" ? "owner" : m.role,
        x: RIGHT_X,
        y: TOP + i * ROW,
        color: `rgb(var(--${personRole(m.name)}))`,
      }),
    );
    brokers.forEach((b, i) => {
      const span = (RIGHT_X - LEFT_X) / (brokers.length + 1);
      nodes.set(`c:${b.contact_id}`, {
        id: `c:${b.contact_id}`,
        label: b.name,
        sub: "broker",
        x: LEFT_X + span * (i + 1),
        y: brokerY,
        color: "rgb(var(--amber))",
      });
    });

    const edges = data.edges
      .map((e) => {
        const a = nodes.get(`c:${e.contact_id}`);
        const b = nodes.get(`u:${e.member}`);
        if (!a || !b) return null;
        return {
          key: `${a.id}|${b.id}`,
          a,
          b,
          width: Math.min(6, 1 + e.touches),
          opacity: recencyOpacity(e.last_touch),
          broker: a.sub === "broker",
        };
      })
      .filter((e): e is NonNullable<typeof e> => e !== null);

    const handoffs = data.handoffs
      .map((h) => {
        const a = nodes.get(`u:${h.from_name}`);
        const b = nodes.get(`u:${h.to_name}`);
        return a && b && a !== b ? { key: h.id, a, b, date: fmtDayShort(h.created_at) } : null;
      })
      .filter((h): h is NonNullable<typeof h> => h !== null);

    return { nodes: [...nodes.values()], edges, handoffs, H, theirs };
  }, [data]);

  const linked = (id: string) =>
    hover === null ||
    hover === id ||
    layout.edges.some(
      (e) => (e.a.id === hover && e.b.id === id) || (e.b.id === hover && e.a.id === id),
    ) ||
    layout.handoffs.some(
      (h) => (h.a.id === hover && h.b.id === id) || (h.b.id === hover && h.a.id === id),
    );

  const insights = buildInsights(layout.theirs);

  return (
    <div className="panel grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_240px] overflow-hidden">
      <div
        className="p-3 overflow-x-auto"
        style={{
          background:
            "radial-gradient(420px 260px at 18% 45%, rgb(var(--cyan) / 0.08), transparent 70%), radial-gradient(420px 260px at 85% 45%, rgb(var(--violet) / 0.08), transparent 70%)",
        }}
      >
        <svg
          viewBox={`0 0 ${W} ${layout.H}`}
          className="w-full min-w-[520px] h-auto"
          role="img"
          aria-label={`Relationship map for ${accountName}`}
        >
          <text
            x={LEFT_X}
            y={30}
            textAnchor="middle"
            className="font-mono"
            style={{ fill: "rgb(var(--cyan))", fontSize: 10, letterSpacing: ".08em" }}
          >
            THEIR SIDE
          </text>
          <text
            x={RIGHT_X}
            y={30}
            textAnchor="middle"
            className="font-mono"
            style={{ fill: "rgb(var(--violet))", fontSize: 10, letterSpacing: ".08em" }}
          >
            OUR SIDE
          </text>
          <g>
            <rect
              x={W / 2 - 80}
              y={18}
              width={160}
              height={34}
              rx={10}
              style={{ fill: "rgb(var(--ink-900))", stroke: "rgb(var(--fg) / 0.12)" }}
            />
            <text
              x={W / 2}
              y={40}
              textAnchor="middle"
              style={{ fill: "rgb(var(--ink-50))", fontSize: 12, fontWeight: 600 }}
            >
              {accountName.length > 22 ? accountName.slice(0, 21) + "…" : accountName}
            </text>
          </g>

          {layout.edges.map((e) => {
            const lit = hover === null || hover === e.a.id || hover === e.b.id;
            const midX = (e.a.x + e.b.x) / 2;
            return (
              <path
                key={e.key}
                d={`M${e.a.x + 16} ${e.a.y} C ${midX} ${e.a.y}, ${midX} ${e.b.y}, ${e.b.x - 16} ${e.b.y}`}
                fill="none"
                strokeLinecap="round"
                style={{
                  stroke: e.broker ? "rgb(var(--amber))" : "rgb(var(--azure))",
                  strokeWidth: e.width,
                  opacity: lit ? e.opacity : 0.06,
                  transition: "opacity .2s",
                }}
              />
            );
          })}

          {layout.handoffs.map((h) => {
            const lit = hover === null || hover === h.a.id || hover === h.b.id;
            const x = RIGHT_X + 34;
            return (
              <g key={h.key} style={{ opacity: lit ? 1 : 0.1, transition: "opacity .2s" }}>
                <path
                  d={`M${h.a.x + 14} ${h.a.y} C ${x + 20} ${h.a.y}, ${x + 20} ${h.b.y}, ${h.b.x + 14} ${h.b.y}`}
                  fill="none"
                  style={{ stroke: "rgb(var(--pink))", strokeWidth: 2, strokeDasharray: "4 5" }}
                />
                <text
                  x={x + 24}
                  y={(h.a.y + h.b.y) / 2 + 4}
                  style={{ fill: "rgb(var(--pink))", fontSize: 10, fontWeight: 500 }}
                >
                  handoff {h.date}
                </text>
              </g>
            );
          })}

          {layout.nodes.map((n) => (
            <g
              key={n.id}
              tabIndex={0}
              role="button"
              aria-label={`${n.label}${n.sub ? `, ${n.sub}` : ""}`}
              onMouseEnter={() => setHover(n.id)}
              onMouseLeave={() => setHover(null)}
              onFocus={() => setHover(n.id)}
              onBlur={() => setHover(null)}
              style={{
                cursor: "pointer",
                opacity: linked(n.id) ? 1 : 0.18,
                transition: "opacity .2s",
                outline: "none",
              }}
            >
              <circle
                cx={n.x}
                cy={n.y}
                r={14}
                style={{
                  fill: n.color,
                  opacity: n.dim ? 0.55 : 1,
                  stroke: "rgb(var(--ink-900))",
                  strokeWidth: 3,
                }}
              />
              <text
                x={n.x}
                y={n.y + 30}
                textAnchor="middle"
                style={{ fill: "rgb(var(--ink-50))", fontSize: 12 }}
              >
                {n.label}
              </text>
              {n.sub && (
                <text
                  x={n.x}
                  y={n.y + 44}
                  textAnchor="middle"
                  style={{ fill: "rgb(var(--mut-500))", fontSize: 10.5 }}
                >
                  {n.sub}
                </text>
              )}
            </g>
          ))}
        </svg>
      </div>

      <aside className="border-t lg:border-t-0 lg:border-l border-fg/[0.07] p-4 space-y-4 text-sm">
        <div>
          <h4 className="section-title mb-2">Reading the map</h4>
          <ul className="space-y-2 text-mut-300">
            <Legend stroke="rgb(var(--azure))" width={5}>
              Talk often
            </Legend>
            <Legend stroke="rgb(var(--azure))" width={2} opacity={0.35}>
              Rarely, or a while ago
            </Legend>
            <Legend stroke="rgb(var(--amber))" width={2}>
              Through the broker
            </Legend>
            <Legend stroke="rgb(var(--pink))" width={2} dashed>
              Handoff
            </Legend>
          </ul>
        </div>
        {insights.map((i) => (
          <div
            key={i.title}
            className="rounded-xl p-3"
            style={{ background: `rgb(var(--${i.role}) / 0.1)` }}
          >
            <div className="font-semibold" style={{ color: `rgb(var(--${i.role}))` }}>
              {i.title}
            </div>
            <div className="text-mut-300 mt-0.5">{i.body}</div>
          </div>
        ))}
        <p className="text-2xs text-mut-500">
          Hover or tab to a person to see who they're connected to.
        </p>
      </aside>
    </div>
  );
}

function Legend({
  stroke,
  width,
  opacity = 1,
  dashed = false,
  children,
}: {
  stroke: string;
  width: number;
  opacity?: number;
  dashed?: boolean;
  children: React.ReactNode;
}) {
  return (
    <li className="flex items-center gap-2.5">
      <svg width="34" height="8" aria-hidden className="shrink-0">
        <line
          x1="2"
          y1="4"
          x2="32"
          y2="4"
          strokeLinecap="round"
          style={{
            stroke,
            strokeWidth: width,
            opacity,
            strokeDasharray: dashed ? "4 5" : undefined,
          }}
        />
      </svg>
      {children}
    </li>
  );
}

function buildInsights(
  theirs: ContactTouch[],
): { title: string; body: string; role: "amber" | "violet" }[] {
  const out: { title: string; body: string; role: "amber" | "violet" }[] = [];
  for (const c of theirs) {
    const d = daysSince(c.last_touch);
    if (d == null || d > 30) {
      out.push({
        title: `${c.name} is going cold`,
        body:
          d == null
            ? "Nobody has logged an interaction with them yet."
            : `Last in touch ${d} days ago.`,
        role: "amber",
      });
    }
  }
  for (const c of theirs) {
    if (c.known_by.length === 1) {
      out.push({
        title: `Only ${c.known_by[0]} knows ${c.name}`,
        body: "If they're away, nobody else has the relationship.",
        role: "violet",
      });
    }
  }
  return out.slice(0, 4);
}
